/**
 * Scaling Up Issues — parking lot for items raised during meetings.
 * Severity (low/medium/high/critical), status (open/in_progress/resolved).
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, AlertOctagon } from "lucide-react";

type Severity = "low" | "medium" | "high" | "critical";
type Status = "open" | "in_progress" | "resolved";

interface Holding {
  id: string;
  name: string;
}
interface Company {
  id: string;
  name: string;
}
interface Issue {
  id: string;
  title: string;
  description: string | null;
  severity: Severity;
  status: Status;
  raisedAt: string;
  resolvedAt: string | null;
}

const SEVERITY_CLASS: Record<Severity, string> = {
  low: "bg-white/5 text-white/60 border-white/10",
  medium: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30",
  high: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  critical: "bg-red-500/15 text-red-300 border-red-500/30",
};

function ScalingUpIssuesInner() {
  const qc = useQueryClient();
  const [holdingId, setHoldingId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const holdingsQuery = useQuery<{ items: Holding[] }>({ queryKey: ["/api/scaling-up/holdings"] });
  const companiesQuery = useQuery<{ items: Company[] }>({
    queryKey: ["/api/scaling-up/holdings", holdingId ?? "", "companies"],
    enabled: holdingId !== null,
  });
  const issuesKey: readonly string[] = ["/api/scaling-up/companies", companyId ?? "", "issues"];
  const issuesQuery = useQuery<{ items: Issue[] }>({
    queryKey: issuesKey,
    enabled: companyId !== null,
  });

  const updateMutation = useMutation({
    mutationFn: async (params: { id: string; status: Status; resolution?: string }) =>
      apiRequest("PATCH", `/api/scaling-up/issues/${params.id}`, {
        status: params.status,
        resolution: params.resolution,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: issuesKey }),
  });

  const holdings = holdingsQuery.data?.items ?? [];
  const companies = companiesQuery.data?.items ?? [];
  const issues = issuesQuery.data?.items ?? [];

  return (
    <div className="min-h-screen bg-[#030407] text-white p-6 lg:p-10">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Scaling Up</div>
          <h1 className="text-2xl lg:text-3xl font-extrabold">Issues — Parking Lot</h1>
        </div>
        <Button size="sm" onClick={() => setShowCreate((v) => !v)} disabled={!companyId}>
          <Plus className="w-4 h-4 mr-2" /> Nowy issue
        </Button>
      </header>

      <Card className="mb-6 border-white/10 bg-white/[0.02]">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={holdingId ?? ""}
            onChange={(e) => {
              setHoldingId(e.target.value || null);
              setCompanyId(null);
            }}
          >
            <option value="">Holding…</option>
            {holdings.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm disabled:opacity-50"
            value={companyId ?? ""}
            onChange={(e) => setCompanyId(e.target.value || null)}
            disabled={!holdingId}
          >
            <option value="">Spółka…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {showCreate && companyId && (
        <CreateIssueForm
          companyId={companyId}
          onCreated={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: issuesKey });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {issuesQuery.isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      )}

      {issues.length === 0 && !issuesQuery.isLoading && companyId && (
        <div className="text-white/40 text-sm flex items-center gap-2">
          <AlertOctagon className="w-4 h-4" /> Brak otwartych issues.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {issues.map((i) => (
          <Card key={i.id} className="border-white/10 bg-white/[0.02]">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="font-semibold">{i.title}</div>
                <Badge className={`border text-[10px] ${SEVERITY_CLASS[i.severity]}`}>
                  {i.severity.toUpperCase()}
                </Badge>
              </div>
              {i.description && <div className="text-sm text-white/60 mb-2">{i.description}</div>}
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-white/40">
                  {new Date(i.raisedAt).toLocaleDateString()}
                </span>
                <div className="flex gap-2">
                  {i.status !== "in_progress" && i.status !== "resolved" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateMutation.mutate({ id: i.id, status: "in_progress" })}
                    >
                      Wdrażamy
                    </Button>
                  )}
                  {i.status !== "resolved" && (
                    <Button
                      size="sm"
                      onClick={() => updateMutation.mutate({ id: i.id, status: "resolved" })}
                    >
                      Rozwiąż
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function CreateIssueForm({
  companyId,
  onCreated,
  onCancel,
}: {
  companyId: string;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<Severity>("medium");

  const create = useMutation({
    mutationFn: async () =>
      apiRequest("POST", `/api/scaling-up/companies/${companyId}/issues`, {
        title,
        description,
        severity,
      }),
    onSuccess: onCreated,
  });

  return (
    <Card className="mb-4 border-white/10 bg-white/[0.04]">
      <CardContent className="p-4 space-y-3">
        <input
          className="w-full bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm"
          placeholder="Tytuł problemu"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          className="w-full bg-black/30 border border-white/10 rounded px-3 py-1.5 text-sm h-20"
          placeholder="Opis (opcjonalnie)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex items-center gap-2">
          <select
            className="bg-white/[0.04] border border-white/10 rounded px-3 py-1.5 text-sm"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as Severity)}
          >
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
            <option value="critical">critical</option>
          </select>
          <Button size="sm" onClick={() => create.mutate()} disabled={!title || create.isPending}>
            {create.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Utwórz
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>
            Anuluj
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ScalingUpIssuesPage() {
  return (
    <AdminGuard role="super_admin">
      <ScalingUpIssuesInner />
    </AdminGuard>
  );
}
